// AuthSignupFlow (contract 06 §8): validate → signup → set temp token → route SignUpMfa.
// Client validation returns field keys (not an exception); server failures surface as AuthError.

import { AuthError } from '../models/auth.error'
import type { SignUpCredentials } from '../models/auth.types'
import { authService, type AuthService } from '../service/AuthService'
import { useAuthStore, type AuthStore } from '../stores/authStore'
import { AuthValidator, type SignupField } from '../validators/AuthValidator'

import { buildGoogleAuthRedirectUrl } from './googleAuthRedirect'

import { markSignup } from '@/features/auth/service/authAttribution'
import { useEnv } from '@/shared/config'

export type SignupOutcome =
  | { ok: true; next: 'signupMfa'; email: string }
  | { ok: false; fieldErrors: Partial<Record<SignupField, string>> }

export interface AuthSignupDeps {
  service?: AuthService
  store?: AuthStore
  validator?: AuthValidator
  redirect?: (url: string) => void
}

export class AuthSignupFlow {
  private readonly service: AuthService
  private readonly store: AuthStore
  private readonly validator: AuthValidator
  private readonly redirect: (url: string) => void

  constructor(deps: AuthSignupDeps = {}) {
    this.service = deps.service ?? authService
    this.store = deps.store ?? useAuthStore()
    this.validator = deps.validator ?? new AuthValidator()
    this.redirect = deps.redirect ?? ((url) => window.location.assign(url))
  }

  async submit(payload: SignUpCredentials, terms: boolean): Promise<SignupOutcome> {
    const validation = this.validator.validateSignup(payload, terms)
    if (!validation.valid) return { ok: false, fieldErrors: validation.fieldErrors }
    try {
      // The form collects one password field (no separate confirmation input), but
      // `/auth/register/` (the legacy v3 controller) rejects a body with no `confirm_password`
      // as 400 `{"confirm_password": ["This field is required."]}` — confirmed against the live
      // endpoint. Mirror the single password into it here, the one place both Signup and
      // Shopify-register funnel through.
      const challenge = await this.service.signup({
        ...payload,
        confirm_password: payload.password,
      })
      markSignup('Email')
      this.store.startMfaChallenge(challenge)
      return { ok: true, next: 'signupMfa', email: payload.email }
    } catch (error) {
      throw AuthError.from(error)
    }
  }

  startGoogleSignupRedirect(): void {
    markSignup('Google')
    this.redirect(buildGoogleAuthRedirectUrl(useEnv().API_APP_URL))
  }
}
